import { useState, useEffect, useCallback, useReducer } from 'react';
import type { Query } from '@tanstack/react-query';

import useMemberCalendlyCall, { CalendlyLogResponse } from '../useMemberCalendlyCall';

import createCallSelector from './createCallSelector';

const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds
const MAX_POLL_DURATION_MS = 30000; // Stop polling after 30 seconds
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // setTimeout fires immediately for longer delays, so wait in chunks

interface UseCalendlyCallDataOptions {
    enabled?: boolean;
}

/**
 * Hook to fetch and manage Calendly call data with polling support
 *
 * @param queryKeyPrefix - Unique prefix for the React Query cache key
 * @param slugs - Calendly event slug prefixes; a call matching any of them counts
 * @param options - Configuration options
 * @returns Call data and polling controls
 *
 * @example
 * // Basic usage
 * const { isScheduled, scheduledTime, startPolling } = useCalendlyCallData('supportCall', ['collective-support-call']);
 *
 * // With conditional enabling
 * const callData = useCalendlyCallData('emCall', ['enrollment-manager-call'], { enabled: shouldFetch });
 */
export default function useCalendlyCallData(
    queryKeyPrefix: string,
    slugs: string[],
    options?: UseCalendlyCallDataOptions
) {
    const { enabled = true } = options ?? {};

    // Polling state
    const [isPolling, setIsPolling] = useState(false);
    const [prevCallTime, setPrevCallTime] = useState<string | null | undefined>(undefined);
    const [pollStartTime, setPollStartTime] = useState<number | null>(null);

    const selector = createCallSelector(slugs);

    const { data, isPending, isSuccess, refetch } = useMemberCalendlyCall(queryKeyPrefix, {
        select: selector,
        enabled: enabled && slugs.some(Boolean),
        refetchInterval: (query: Query<CalendlyLogResponse[]>) => {
            if (!isPolling) {
                return false;
            }

            const rawData = query.state.data;
            const selectedData = rawData ? selector(rawData) : undefined;
            const currentScheduledTime = selectedData?.scheduledTime;

            // For reschedules (prevCallTime is set), wait for time to CHANGE
            if (prevCallTime !== undefined) {
                if (currentScheduledTime && currentScheduledTime !== prevCallTime) {
                    return false; // Stop polling - time changed
                }
            } else if (currentScheduledTime) {
                // For new schedules, stop when scheduledTime becomes truthy
                return false;
            }

            return POLL_INTERVAL_MS; // Continue polling
        },
        // When not polling, data is stable
        staleTime: isPolling ? 0 : Infinity,
    });

    const scheduledTime = data?.scheduledTime;
    const callEndsAt = data?.endTime ?? scheduledTime;

    // The query never goes stale, so re-render when the selected call ends to let the selector drop it.
    const [reevaluations, reevaluate] = useReducer((count: number) => count + 1, 0);
    useEffect(() => {
        if (!callEndsAt) {
            return undefined;
        }
        const delay = new Date(callEndsAt).getTime() - Date.now();
        if (delay <= 0) {
            return undefined;
        }
        const timeoutId = setTimeout(reevaluate, Math.min(delay, MAX_TIMEOUT_MS));
        return () => clearTimeout(timeoutId);
    }, [callEndsAt, reevaluations]);

    /**
     * Start polling for updated call data
     * @param originalTime - For reschedules, pass the current scheduledTime so polling waits for it to CHANGE
     */
    const startPolling = useCallback((originalTime?: string | null) => {
        setIsPolling(true);
        setPrevCallTime(originalTime);
        setPollStartTime(Date.now());
    }, []);

    // Stop polling and reset polling state
    const stopPolling = useCallback(() => {
        setIsPolling(false);
        setPrevCallTime(undefined);
        setPollStartTime(null);
    }, []);

    // Auto-stop polling when scheduledTime appears (new) or changes (reschedule)
    useEffect(() => {
        if (!isPolling) {
            return;
        }

        if (prevCallTime !== undefined) {
            // Reschedule: stop when time changes
            if (scheduledTime && scheduledTime !== prevCallTime) {
                stopPolling();
            }
        } else if (scheduledTime) {
            // New schedule: stop when time appears
            stopPolling();
        }
    }, [isPolling, scheduledTime, prevCallTime, stopPolling]);

    // Failsafe: stop polling after max duration if webhook never arrives
    useEffect(() => {
        if (!pollStartTime) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            stopPolling();
        }, MAX_POLL_DURATION_MS);

        return () => clearTimeout(timeoutId);
    }, [pollStartTime, stopPolling]);

    return {
        isScheduled: data?.isScheduled ?? false,
        slug: data?.slug,
        scheduledTime: scheduledTime ?? null,
        endTime: data?.endTime ?? null,
        rescheduleUrl: data?.rescheduleUrl,
        joinUrl: data?.joinUrl,
        lastCallEndedAt: data?.lastCallEndedAt,
        isPending,
        isSuccess,
        refetch,
        startPolling,
        stopPolling,
        isPolling,
    };
}
